/**
 * FacilityDestroyedToast.tsx — DD Bug#10b
 *
 * Transient 1.5s notification shown when any facility is destroyed or
 * decommissioned via a card effect (Mercenaries, Coal_Lobby_Influence,
 * Climate_Protest, Wildfire, Cyberattack, etc.).
 * "P{N}'s {facilityId} destroyed!" — auto-dismisses after 1.5s.
 * Click to dismiss immediately.
 */

import type React from "react";
import { useEffect } from "react";
import { FACILITIES } from "../../store/facilities.js";
import type { SeatIndex } from "../../store/game-store.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./FacilityDestroyedToast.module.css";

const PLAYER_LABELS = ["Player 1", "Player 2", "Player 3", "Player 4"] as const;

function playerLabel(seat: SeatIndex): string {
  return PLAYER_LABELS[seat] ?? `P${seat + 1}`;
}

function facilityLabel(facilityId: string): string {
  const def = FACILITIES.find((f) => f.id === facilityId);
  return def?.name ?? facilityId.replace(/_/g, " ");
}

const AUTO_DISMISS_MS = 1_500;

export function FacilityDestroyedToast(): React.ReactElement | null {
  const lastFacilityDestroyed = useGameStore((s) => s.lastFacilityDestroyed);
  const clearFacilityDestroyedToast = useGameStore((s) => s.clearFacilityDestroyedToast);

  useEffect(() => {
    if (lastFacilityDestroyed === null) return;
    const timer = window.setTimeout(() => {
      clearFacilityDestroyedToast();
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [lastFacilityDestroyed, clearFacilityDestroyedToast]);

  if (lastFacilityDestroyed === null) return null;

  const { seat, facilityId } = lastFacilityDestroyed;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={clearFacilityDestroyedToast}
      aria-label="Dismiss facility destroyed notification"
      data-testid="facility-destroyed-toast"
      data-seat={seat}
      data-facility-id={facilityId}
    >
      <div className={styles.icon}>💥</div>
      <div className={styles.body}>
        <span className={styles.playerLabel}>{playerLabel(seat)}</span>
        <span className={styles.apostrophe}>'s </span>
        <span className={styles.facilityLabel}>{facilityLabel(facilityId)}</span>
      </div>
      <div className={styles.destroyed}>DESTROYED</div>
    </button>
  );
}
